import React, { createContext, useEffect } from 'react'
import { useGet, RestfulProvider } from 'restful-react'
import { endpoints, API_BASE_URL } from 'app/api/endpoints'
import { ApiError, User } from 'EstraSuppliers'
import {
  getAuthFromLocalStorage,
  removeAuthFromLocalStorage,
  storeAuthToLocalStorage,
} from './persistAuthToken'

type AuthenticationContext = {
  authToken: string | null
  actor: User | null
  loadingActor: boolean
  updateAuthToken: (authToken: string | null) => void
  refetchActor: () => void
  executeLogout: () => void
}

const initialAuthToken = getAuthFromLocalStorage() || null

export const AuthenticationContext = createContext<AuthenticationContext | null>(
  null
)

export const AuthenticationProvider: React.FC = ({ children }) => {
  const [authToken, setAuthToken] = React.useState(initialAuthToken)
  const [actor, setActor] = React.useState<User | null>(null)
  const [loadingActor, setLoadingActor] = React.useState(true)

  const API_HEADERS = {
    'Content-Type': 'application/json',
    Authorization: authToken ?? '',
  }

  // Lazy fetching of user
  // We will execute the fetch call once we have the authToken
  const { data: userData, refetch: fetchUser, error } = useGet<User, ApiError>({
    base: API_BASE_URL,
    path: endpoints.me,
    requestOptions: () => ({
      headers: API_HEADERS,
    }),
  })

  // As soon as we get an auth token we can store it and fetch user
  useEffect(() => {
    if (authToken !== null && authToken !== undefined) {
      storeAuthToLocalStorage(authToken as string)
      fetchUser()
    } else {
      removeAuthFromLocalStorage()
      setActor(null)
    }
  }, [authToken])

  // Updating actor so it will be available to our consumers
  const actorIdentifier = (userData && userData.id) || null
  useEffect(() => {
    setActor(userData)
    if (actorIdentifier || error) {
      setLoadingActor(false)
    }
  }, [actorIdentifier, error, userData])

  return (
    <AuthenticationContext.Provider
      value={{
        authToken,
        actor,
        loadingActor: loadingActor,
        updateAuthToken: token => {
          setAuthToken(token)
        },
        refetchActor: () => {
          fetchUser()
        },
        executeLogout: () => {
          setAuthToken(null)
        },
      }}
    >
      <RestfulProvider
        base={API_BASE_URL}
        onError={error => {
          // If 401 remove the token so it will get redirected to login
          if (error.status === 401) {
            setAuthToken(null)
          }
        }}
        requestOptions={() => ({
          headers: API_HEADERS,
        })}
      >
        {/* {loadingActor ? null : children} */}
        {children}
      </RestfulProvider>
    </AuthenticationContext.Provider>
  )
}
